// src/components/marketing/BoostFlow.tsx
// Track E2.4 — boost an existing Facebook / Instagram post in two clicks.
//
// Left pane: list of recent page posts (thumbnail, preview, engagement).
// Right pane: form (daily budget, duration, optional name/targeting).
// Submit → POST /admin/campaigns/boost → navigate to the new campaign's
// detail screen so ops can watch `status=pending → paused` as the task
// runs (A4).
//
// Reads decouple cleanly from writes here: the backend caches the
// page-posts listing 5 min per org+limit, so the refresh button forces
// a fresh pull when ops just published a new post they want to boost.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ExternalLink, Heart, Loader2, MessageCircle,
  RefreshCw, Search, Send, Share2, Zap,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  ApiError, createBoost, getAccountMeta, listPagePosts,
  type AccountMetaOut, type BoostCreateIn, type BoostDestinationKind,
  type PagePost,
} from '@/lib/api_campaigns';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props {
  addNotification?: Notifier;
}

const DEFAULT_DAILY_BUDGET_MAJOR = 100; // 100 in account currency (e.g. MX$100 or US$100)
const DEFAULT_DURATION_DAYS = 7;

function _currencySymbol(code: string): string {
  if (code === 'MXN') return 'MX$';
  if (code === 'USD') return 'US$';
  return code || '$';
}

export function BoostFlow({ addNotification }: Props) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<PagePost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [dailyBudget, setDailyBudget] = useState<string>(String(DEFAULT_DAILY_BUDGET_MAJOR));
  const [durationDays, setDurationDays] = useState<string>(String(DEFAULT_DURATION_DAYS));
  const [nameOverride, setNameOverride] = useState('');
  const [targetingText, setTargetingText] = useState<string>('');
  const [destinationKind, setDestinationKind] = useState<BoostDestinationKind>('awareness');
  const [destinationValue, setDestinationValue] = useState<string>('');
  const [accountMeta, setAccountMeta] = useState<AccountMetaOut | null>(null);

  useEffect(() => {
    getAccountMeta().then(setAccountMeta).catch(() => {
      // No-op: si falla, defaulteamos a labels genericos.
    });
  }, []);
  const currency = accountMeta?.currency || '';
  const currencySym = _currencySymbol(currency);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listPagePosts({ limit: 50, refresh });
      setPosts(rows);
      // Auto-select the most recent post if nothing is selected yet.
      if (rows.length && selectedId === null) setSelectedId(rows[0].id);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error desconocido');
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(false); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return posts;
    return posts.filter((p) => p.message.toLowerCase().includes(q));
  }, [posts, search]);

  const selected = posts.find((p) => p.id === selectedId) ?? null;

  // Targeting libre: texto plano que el server interpreta via LLM
  // (mismo flujo que el director). Sin JSON crudo — el operator escribe
  // "CDMX y Guadalajara, dueños de negocio 30-55, interesados en marketing"
  // y el backend resuelve geo/intereses/comportamientos via tools.

  const dailyCents = useMemo(() => {
    const n = parseFloat(dailyBudget);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  }, [dailyBudget]);

  const durationNum = useMemo(() => {
    const n = parseInt(durationDays, 10);
    if (!Number.isFinite(n) || n < 1) return null;
    return n;
  }, [durationDays]);

  const canSubmit =
    !submitting &&
    selected !== null &&
    dailyCents !== null && dailyCents >= 100 &&
    durationNum !== null && durationNum >= 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selected) return;

    const payload: BoostCreateIn = {
      page_post_id: selected.id,
      daily_budget_cents: dailyCents!,
      duration_days: durationNum!,
      destination_kind: destinationKind,
    };
    if (destinationValue.trim()) payload.destination_value = destinationValue.trim();
    if (targetingText.trim()) payload.targeting_text = targetingText.trim();
    if (nameOverride.trim()) payload.name = nameOverride.trim();

    setSubmitting(true);
    try {
      const campaign = await createBoost(payload);
      addNotification?.({
        type: 'success',
        title: 'Boost encolado',
        message: `"${campaign.name}" está en cola para publicar en Meta.`,
        duration: 4000,
      });
      navigate(`/m/mkt-meta/campaigns/${campaign.id}`);
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : (err?.message || 'Error desconocido');
      addNotification?.({
        type: 'error',
        title: 'No se pudo crear el boost',
        message: msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-5">
      {/* ── Left: post picker ────────────────────────────── */}
      <div className="o-card p-5 flex flex-col min-h-[600px]">
        <div className="flex items-center gap-3 mb-3">
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Zap size={18} className="text-amber-300" />
            Boost de post existente
          </h1>
          <div className="flex-1" />
          <label className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm">
            <Search size={14} className="opacity-60" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar texto…"
              className="bg-transparent outline-none w-48"
            />
          </label>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="o-btn-ghost flex items-center gap-1 text-sm"
            title="Ignorar caché y recargar desde Meta"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refrescar
          </button>
        </div>

        {loadError && /page_id not configured|page_id no configurado/i.test(loadError) ? (
          <div className="mb-3 p-4 rounded-lg border border-amber-500/40 bg-amber-500/10 text-sm text-amber-100 flex items-start gap-3">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">No hay una página de Facebook conectada</div>
              <div className="opacity-80 mt-1">
                Para boostear posts existentes primero conecta una página desde Empresa → Negocios → (selecciona un negocio) → Meta.
              </div>
              <a
                href="#/empresa/negocios"
                className="inline-block mt-2 px-3 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-xs font-medium"
              >
                Ir a Negocios
              </a>
            </div>
          </div>
        ) : loadError && (
          <div className="mb-3 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">No se pudieron cargar los posts</div>
              <div className="opacity-80 break-words">{loadError}</div>
              <div className="opacity-60 mt-1 text-xs">
                Reintenta en un momento o verifica la conexión en Empresa → Negocios → Meta.
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-auto -mx-2 px-2">
          {loading && posts.length === 0 && (
            <div className="h-40 flex items-center justify-center opacity-60">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}
          {!loading && filtered.length === 0 && !loadError && (
            <div className="h-40 flex flex-col items-center justify-center text-sm text-white/50">
              <p>No hay posts para promocionar.</p>
              <p className="text-xs mt-1">Publica algo en la página y presiona Refrescar.</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filtered.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                selected={post.id === selectedId}
                onSelect={() => setSelectedId(post.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: form ──────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="o-card p-5 space-y-4 self-start sticky top-4">
        <h2 className="text-base font-semibold">Configuración del boost</h2>

        {selected ? (
          <div className="flex items-start gap-3 p-3 rounded-md bg-white/5 border border-white/10">
            {selected.thumbnail_url ? (
              <img
                src={selected.thumbnail_url}
                alt=""
                className="w-14 h-14 rounded-md object-cover shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-md bg-white/10 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-xs text-white/50">Post seleccionado</div>
              <div className="text-sm line-clamp-3">
                {selected.message_preview || '(sin texto)'}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-3 rounded-md bg-white/5 border border-dashed border-white/15 text-sm text-white/50">
            Selecciona un post de la izquierda para continuar.
          </div>
        )}

        <LabeledField
          label="¿Para qué quieres optimizar?"
          hint="Determina el objetivo Meta + optimization goal. Awareness = boost puro (más views/likes). Los demás optimizan delivery hacia esa acción específica del post."
        >
          <select
            value={destinationKind}
            onChange={(e) => setDestinationKind(e.target.value as BoostDestinationKind)}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          >
            <option value="awareness">Spread del post (awareness + post engagement)</option>
            <option value="whatsapp">Mensajes de WhatsApp (CTAs WA del post)</option>
            <option value="messenger">Mensajes de Messenger</option>
            <option value="phone_call">Llamadas telefónicas</option>
            <option value="website">Tráfico al sitio web</option>
          </select>
          {destinationKind !== 'awareness' && (
            <input
              type="text"
              value={destinationValue}
              onChange={(e) => setDestinationValue(e.target.value)}
              placeholder={
                destinationKind === 'whatsapp' ? '5215599631809 (con código de país)' :
                destinationKind === 'website' ? 'https://...' :
                destinationKind === 'phone_call' ? '+5215599631809' :
                'opcional'
              }
              className="mt-2 w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50 text-sm"
            />
          )}
        </LabeledField>

        <LabeledField
          label={`Presupuesto diario (${currency || 'moneda del ad account'})`}
          hint={`Mínimo ${currencySym}1 — Meta rechaza presupuestos inferiores. Se cobra en la moneda del ad account (${currency || '—'}).`}
        >
          <input
            type="number"
            step="0.01"
            min="1"
            value={dailyBudget}
            onChange={(e) => setDailyBudget(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
          {dailyCents !== null && dailyCents < 100 && (
            <p className="text-xs text-amber-300 mt-1">Mínimo {currencySym}1.</p>
          )}
        </LabeledField>

        <LabeledField label="Duración (días)" hint={`Por defecto ${DEFAULT_DURATION_DAYS} días desde hoy.`}>
          <input
            type="number"
            min="1"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
        </LabeledField>

        <LabeledField label="Nombre (opcional)" hint="Si lo dejas vacío, Meta genera un nombre desde el ID del post.">
          <input
            type="text"
            value={nameOverride}
            onChange={(e) => setNameOverride(e.target.value)}
            placeholder={selected ? `Boost ${selected.id}` : 'Boost …'}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50"
          />
        </LabeledField>

        <LabeledField
          label="Audiencia (texto libre)"
          hint="Describe a quién va dirigido. La IA resuelve ciudades, intereses, comportamientos y edad — sin pegar JSON ni IDs."
        >
          <textarea
            value={targetingText}
            onChange={(e) => setTargetingText(e.target.value)}
            placeholder={
              'Ej: "Dueños de negocio en CDMX y Guadalajara, 30-55 años, ' +
              'interesados en marketing digital y publicidad."\n' +
              'Si lo dejas vacío usamos audiencia broad del país de la cuenta.'
            }
            rows={5}
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50 text-sm"
          />
        </LabeledField>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Encolando…
            </>
          ) : (
            <>
              <Send size={14} />
              Lanzar boost
            </>
          )}
        </button>

        <p className="text-xs text-white/40 leading-relaxed">
          El boost se crea en <strong>pending</strong>, sube los assets a
          Meta y queda <strong>activo</strong> automáticamente. Si necesitas
          revisar antes, pausa desde el detalle de la campaña.
        </p>
      </form>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Post card
// ═══════════════════════════════════════════════════════════════

function PostCard({
  post,
  selected,
  onSelect,
}: {
  post: PagePost;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={`rounded-lg border transition-colors cursor-pointer overflow-hidden flex flex-col ${
        selected
          ? 'border-[var(--acc1,#00e5ff)]/60 bg-[var(--acc1,#00e5ff)]/10'
          : 'border-white/10 bg-white/5 hover:bg-white/10'
      }`}
    >
      {post.thumbnail_url ? (
        <img
          src={post.thumbnail_url}
          alt=""
          className="w-full h-32 object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-32 bg-white/5 flex items-center justify-center text-white/30 text-xs">
          sin preview
        </div>
      )}

      <div className="p-3 flex flex-col gap-2 flex-1">
        <p className="text-sm line-clamp-3 min-h-[60px]">
          {post.message_preview || <span className="text-white/40">(sin texto)</span>}
        </p>

        <div className="flex items-center gap-3 text-xs text-white/50">
          <span className="flex items-center gap-1" title="Likes">
            <Heart size={11} />
            {abbrev(post.likes_count)}
          </span>
          <span className="flex items-center gap-1" title="Comentarios">
            <MessageCircle size={11} />
            {abbrev(post.comments_count)}
          </span>
          <span className="flex items-center gap-1" title="Compartidos">
            <Share2 size={11} />
            {abbrev(post.shares_count)}
          </span>
          <span className="flex-1 text-right opacity-70">
            {formatDate(post.created_time)}
          </span>
        </div>

        {post.permalink_url && (
          <a
            href={post.permalink_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-white/50 hover:text-white flex items-center gap-1 self-start"
          >
            <ExternalLink size={11} />
            Ver en Facebook
          </a>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════

function LabeledField({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-white/50">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-xs text-white/40 mt-1 block">{hint}</span>}
    </label>
  );
}

function abbrev(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function formatDate(iso: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default BoostFlow;
